var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Proctoring capture-resolution test bench (admin) — full-screen layout.
 *
 * WHY: in cost-optimized/Azure interviews, proctoring samples the candidate's
 * SHARED SCREEN and downscales it to a fixed width before the backend runs
 * MediaPipe FaceMesh. During coding the webcam shrinks to a small PiP tile on
 * that screen, so at the downscaled width the face becomes too few pixels to
 * detect (0 faces for the whole coding phase).
 *
 * This bench reproduces that pipeline faithfully: it draws your webcam as a
 * resizable PiP tile inside a full "screen"-sized canvas, downscales the whole
 * canvas to a chosen capture width (exactly like the live path), and STREAMS the
 * frames over a WebSocket to the SAME backend FaceMesh. The returned landmarks
 * are drawn live as a mesh overlay (like the report), so you can see in real time
 * whether — and at what resolution / tile size — a face is detected, then save
 * the winning width as the live capture width. Nothing is persisted.
 *
 * Layout: a fixed-width side column holds all controls/config; the rest of the
 * viewport shows the full live stream + overlay.
 */
import { useProctoringConfigAdmin, useUpdateProctoringConfig } from './voice-admin-api';
import { useToast } from '@/hoc';
import { useConfigContext } from '@/hoc/context/config-provider';
import { composeToScreenFraction } from '@/utils/proctor-layout';
import { Camera, Loader2, Save, Square, CheckCircle2, XCircle, Pause, Play } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../ui';
const SCREEN_PRESETS = [
    { label: '1366 × 768 (laptop)', w: 1366, h: 768 },
    { label: '1920 × 1080 (FHD)', w: 1920, h: 1080 },
    { label: '2560 × 1440 (QHD)', w: 2560, h: 1440 },
];
const WIDTH_OPTIONS = [320, 480, 640, 720, 960, 1280];
const CODING_PIP_WIDTH = 340; // matches the live coding-editor camera tile (w-[340px])
const FRAME_INTERVAL_MS = 300; // ~3/s stream to the detector
function proctoringWsUrl(webSocketUrl) {
    if (!webSocketUrl)
        return '';
    return webSocketUrl.replace(/ws\/interview\/?$/, 'ws/proctoring-test/');
}
// Draw a video into a destination rect with object-fit: cover (center-crop),
// mirrored horizontally to match the live self-view.
function drawCover(ctx, video, dx, dy, dw, dh) {
    const vw = video.videoWidth || dw;
    const vh = video.videoHeight || dh;
    const scale = Math.max(dw / vw, dh / vh);
    const sw = dw / scale;
    const sh = dh / scale;
    const sx = (vw - sw) / 2;
    const sy = (vh - sh) / 2;
    ctx.save();
    ctx.translate(dx + dw, dy); // mirror
    ctx.scale(-1, 1);
    ctx.drawImage(video, sx, sy, sw, sh, 0, 0, dw, dh);
    ctx.restore();
}
const ProctoringTestPanel = () => {
    var _a, _b, _c;
    const { showToast } = useToast();
    const { config: { webSocketUrl }, } = useConfigContext();
    const { data: liveConfig } = useProctoringConfigAdmin();
    const updateConfig = useUpdateProctoringConfig();
    const videoRef = useRef(null);
    const screenCanvasRef = useRef(null); // composed "screen" + overlay
    const captureCanvasRef = useRef(null); // downscaled frame streamed
    const streamRef = useRef(null);
    const wsRef = useRef(null);
    const sendTimerRef = useRef(null);
    const facesRef = useRef([]); // latest detected faces for the overlay (one per person)
    const lastResultRef = useRef(null); // latest detection for the HUD
    // live-readable copies for the send/draw loops (avoid stale closures)
    const captureWRef = useRef(320);
    const pipWRef = useRef(CODING_PIP_WIDTH);
    const screenRef = useRef(SCREEN_PRESETS[1]);
    const streamingRef = useRef(true);
    // New camera-feed pipeline: detect on the raw webcam only, then map the mesh
    // onto the PiP box (exactly what the live feature + report overlay do).
    const detectModeRef = useRef('camera');
    const camVisibleRef = useRef(true); // simulate camVisible:false → overlay hidden
    const [camState, setCamState] = useState('idle');
    const [camError, setCamError] = useState('');
    const [wsState, setWsState] = useState('idle');
    const [wsError, setWsError] = useState('');
    const [streaming, setStreaming] = useState(true);
    const [screenIdx, setScreenIdx] = useState(1);
    const [pipW, setPipW] = useState(CODING_PIP_WIDTH);
    const [captureW, setCaptureW] = useState(320);
    const [detectMode, setDetectMode] = useState('camera');
    const [camVisibleSim, setCamVisibleSim] = useState(true);
    const [last, setLast] = useState(null);
    const [trials, setTrials] = useState([]);
    const trialId = useRef(0);
    const screen = SCREEN_PRESETS[screenIdx];
    useEffect(() => {
        captureWRef.current = captureW;
    }, [captureW]);
    useEffect(() => {
        pipWRef.current = pipW;
    }, [pipW]);
    useEffect(() => {
        screenRef.current = screen;
    }, [screen]);
    useEffect(() => {
        streamingRef.current = streaming;
    }, [streaming]);
    useEffect(() => {
        detectModeRef.current = detectMode;
    }, [detectMode]);
    useEffect(() => {
        camVisibleRef.current = camVisibleSim;
    }, [camVisibleSim]);
    const pipInCapture = useMemo(() => Math.round((pipW * captureW) / screen.w), [pipW, captureW, screen.w]);
    // ── webcam ────────────────────────────────────────────────────────────────
    const startCam = () => __awaiter(void 0, void 0, void 0, function* () {
        setCamState('starting');
        setCamError('');
        try {
            const stream = yield navigator.mediaDevices.getUserMedia({ video: true, audio: false });
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                yield videoRef.current.play().catch(() => undefined);
            }
            setCamState('on');
            connectWs();
        }
        catch (e) {
            setCamState('error');
            setCamError((e === null || e === void 0 ? void 0 : e.message) || 'Could not access the camera.');
        }
    });
    const stopCam = () => {
        var _a;
        closeWs();
        (_a = streamRef.current) === null || _a === void 0 ? void 0 : _a.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        if (videoRef.current)
            videoRef.current.srcObject = null;
        facesRef.current = [];
        setCamState('idle');
    };
    // ── websocket ─────────────────────────────────────────────────────────────
    const connectWs = () => {
        const url = proctoringWsUrl(webSocketUrl);
        if (!url) {
            setWsState('error');
            setWsError('No WebSocket URL configured.');
            return;
        }
        setWsState('connecting');
        setWsError('');
        let ws;
        try {
            ws = new WebSocket(url);
        }
        catch (e) {
            setWsState('error');
            setWsError((e === null || e === void 0 ? void 0 : e.message) || 'Could not open WebSocket.');
            return;
        }
        wsRef.current = ws;
        ws.onmessage = (evt) => {
            let msg;
            try {
                msg = JSON.parse(evt.data);
            }
            catch (_a) {
                return;
            }
            if (msg.type === 'ready') {
                setWsState('ready');
                return;
            }
            if (msg.type === 'error') {
                setWsState('error');
                setWsError(msg.error || 'Detector error');
                return;
            }
            if (msg.type === 'result') {
                if (!msg.ok) {
                    setWsError(msg.error || 'Detection failed');
                    return;
                }
                setWsError('');
                // Prefer the per-face list; fall back to the single primary face.
                facesRef.current = msg.detected
                    ? msg.faces && msg.faces.length
                        ? msg.faces
                        : msg.face
                            ? [msg.face]
                            : []
                    : [];
                lastResultRef.current = msg;
                setLast(msg);
            }
        };
        ws.onerror = () => {
            setWsState('error');
            setWsError('WebSocket connection error.');
        };
        ws.onclose = () => {
            if (sendTimerRef.current)
                clearInterval(sendTimerRef.current);
            sendTimerRef.current = null;
            setWsState((s) => (s === 'error' ? s : 'idle'));
        };
    };
    const closeWs = () => {
        var _a;
        if (sendTimerRef.current)
            clearInterval(sendTimerRef.current);
        sendTimerRef.current = null;
        try {
            (_a = wsRef.current) === null || _a === void 0 ? void 0 : _a.close();
        }
        catch (_b) {
            /* ignore */
        }
        wsRef.current = null;
        setWsState('idle');
    };
    // Stream composed+downscaled frames while the socket is ready and not paused.
    useEffect(() => {
        if (wsState !== 'ready' || camState !== 'on')
            return undefined;
        sendTimerRef.current = setInterval(() => {
            if (!streamingRef.current)
                return;
            const ws = wsRef.current;
            const sc = screenCanvasRef.current;
            const cc = captureCanvasRef.current;
            const v = videoRef.current;
            if (!ws || ws.readyState !== WebSocket.OPEN || !sc || !cc)
                return;
            const ctx = cc.getContext('2d');
            if (!ctx)
                return;
            if (detectModeRef.current === 'camera' && v && v.videoWidth) {
                // NEW pipeline: detect on the RAW webcam frame only (un-mirrored, like
                // the live userStream path). Landmarks come back normalized to the
                // camera frame and are mapped onto the PiP box in the draw loop.
                const w = captureWRef.current || 320;
                const h = Math.round((v.videoHeight / v.videoWidth) * w);
                cc.width = w;
                cc.height = h;
                ctx.drawImage(v, 0, 0, w, h);
            }
            else {
                // Legacy pipeline: detect on the whole downscaled screen composite.
                const w = captureWRef.current || 320;
                const h = Math.round((sc.height / sc.width) * w);
                cc.width = w;
                cc.height = h;
                ctx.drawImage(sc, 0, 0, w, h);
            }
            ws.send(JSON.stringify({ type: 'frame', frame: cc.toDataURL('image/jpeg', 0.6) }));
        }, FRAME_INTERVAL_MS);
        return () => {
            if (sendTimerRef.current)
                clearInterval(sendTimerRef.current);
            sendTimerRef.current = null;
        };
    }, [wsState, camState]);
    // ── compose + overlay draw loop (continuous) ──────────────────────────────
    useEffect(() => {
        if (camState !== 'on')
            return undefined;
        let raf = 0;
        const draw = () => {
            var _a, _b, _c;
            const sc = screenCanvasRef.current;
            const v = videoRef.current;
            if (sc && v) {
                const scr = screenRef.current;
                sc.width = scr.w;
                sc.height = scr.h;
                const ctx = sc.getContext('2d');
                if (ctx) {
                    ctx.fillStyle = '#0b1220';
                    ctx.fillRect(0, 0, sc.width, sc.height);
                    const pw = pipWRef.current;
                    const ph = Math.round((pw * 9) / 16);
                    const margin = Math.round(scr.w * 0.02);
                    const px = sc.width - pw - margin;
                    const py = margin;
                    drawCover(ctx, v, px, py, pw, ph);
                    ctx.strokeStyle = 'rgba(255,255,255,0.25)';
                    ctx.strokeRect(px, py, pw, ph);
                    const res = lastResultRef.current;
                    const fc = (res === null || res === void 0 ? void 0 : res.detected) ? (_a = res.face_count) !== null && _a !== void 0 ? _a : 0 : 0;
                    // Report parity: boundary tints red when a violation (multiple faces)
                    // is active, white otherwise.
                    const border = fc > 1 ? '#ef4444' : 'rgba(255,255,255,0.9)';
                    const hudLines = [`faces: ${fc}`];
                    if (fc > 1)
                        hudLines.push('⚠ multiple faces');
                    if (res === null || res === void 0 ? void 0 : res.detected)
                        hudLines.push(`conf ${((_b = res.confidence) !== null && _b !== void 0 ? _b : 0).toFixed(2)} · ${(_c = res.elapsed_ms) !== null && _c !== void 0 ? _c : 0}ms`);
                    if (detectModeRef.current === 'camera') {
                        // NEW pipeline: map camera-space landmarks onto the PiP box via the
                        // SAME composeToScreenFraction the report overlay uses. The bench
                        // knows the PiP rect exactly, so this deterministically validates the
                        // camBox placement + mirror + camVisible-hide with no screen-coord
                        // guesswork. camVisible:false → draw nothing (overlay hidden).
                        const layout = {
                            t: 0,
                            camBox: { x: px / sc.width, y: py / sc.height, w: pw / sc.width, h: ph / sc.height },
                            mirrored: true, // the PiP is drawn mirrored (drawCover)
                            camVisible: camVisibleRef.current,
                            fit: 'cover',
                        };
                        const toXY = (nx, ny) => {
                            const s = composeToScreenFraction(nx, ny, layout);
                            return s ? [s.x * sc.width, s.y * sc.height] : [nx * sc.width, ny * sc.height];
                        };
                        if (layout.camVisible) {
                            // A boundary per detected face (not only the primary).
                            facesRef.current.forEach((f) => drawMesh(ctx, f, toXY, border));
                            // HUD anchored to the PiP box (top-left of the tile).
                            drawHud(ctx, px, py, hudLines);
                        }
                    }
                    else {
                        // Legacy: landmarks are normalized to the whole composite frame.
                        const toXY = (nx, ny) => [
                            nx * sc.width,
                            ny * sc.height,
                        ];
                        facesRef.current.forEach((f) => drawMesh(ctx, f, toXY, border));
                        drawHud(ctx, 0, 0, hudLines);
                    }
                }
            }
            raf = requestAnimationFrame(draw);
        };
        raf = requestAnimationFrame(draw);
        return () => cancelAnimationFrame(raf);
    }, [camState]);
    useEffect(() => () => stopCam(), []); // cleanup on unmount
    const logCurrent = () => {
        var _a, _b;
        if (!last)
            return;
        const t = {
            id: (trialId.current += 1),
            screenW: screen.w,
            pipW,
            captureW,
            faceCount: (_a = last.face_count) !== null && _a !== void 0 ? _a : 0,
            detected: !!last.detected,
            ms: (_b = last.elapsed_ms) !== null && _b !== void 0 ? _b : 0,
        };
        setTrials((prev) => [t, ...prev].slice(0, 30));
    };
    const saveAsLive = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            yield updateConfig.mutateAsync({ captureWidth: captureW });
            showToast({ message: `Live capture width set to ${captureW}px`, status: 'success' });
        }
        catch (_a) {
            showToast({ message: 'Could not save the live capture width', status: 'error' });
        }
    });
    const labelCls = 'block text-xs font-medium text-dash-muted mb-1';
    const selectCls = 'w-full rounded-md border border-dash-border bg-dash-surface px-2 py-1.5 text-sm text-dash-text focus:outline-none focus:ring-1 focus:ring-dash-accent';
    const statusText = camState === 'starting'
        ? 'Starting camera…'
        : camState !== 'on'
            ? 'Camera off'
            : wsState === 'ready'
                ? 'Detector live'
                : wsState === 'error'
                    ? 'Detector error'
                    : 'Connecting…';
    const statusTone = camState !== 'on'
        ? 'muted'
        : wsState === 'ready'
            ? 'success'
            : wsState === 'error'
                ? 'danger'
                : 'muted';
    const tonePill = statusTone === 'success'
        ? 'bg-dash-success/15 text-dash-successInk'
        : statusTone === 'danger'
            ? 'bg-dash-danger/15 text-dash-dangerInk'
            : 'bg-dash-surface text-dash-muted';
    return (_jsxs("div", { className: 'flex h-full min-h-0 w-full', children: [_jsxs("aside", { className: 'flex w-[360px] shrink-0 flex-col gap-4 overflow-y-auto border-r border-dash-border bg-dash-card p-5', children: [_jsxs("div", { className: 'flex flex-col gap-2', children: [_jsxs("div", { className: 'flex items-center justify-between', children: [_jsxs("span", { className: `inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${tonePill}`, children: [_jsx("span", { className: `h-1.5 w-1.5 rounded-full ${statusTone === 'success' ? 'animate-pulse bg-dash-success' : 'bg-current'}` }), statusText] }), camState === 'on' && (_jsxs("button", { type: 'button', onClick: stopCam, className: 'inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-dash-muted transition-colors hover:bg-dash-surface hover:text-dash-text', children: [_jsx(Square, { className: 'h-3 w-3' }), " Stop"] }))] }), camState === 'error' && _jsx("span", { className: 'text-xs text-dash-dangerInk', children: camError }), wsError && (_jsx("div", { className: 'rounded-md bg-dash-danger/10 px-3 py-2 text-xs text-dash-dangerInk', children: wsError }))] }), _jsx("div", { className: 'text-[11px] font-semibold uppercase tracking-wide text-dash-muted', children: "Simulation" }), _jsxs("div", { children: [_jsx("label", { className: labelCls, children: "Detection pipeline" }), _jsxs("select", { className: selectCls, value: detectMode, onChange: (e) => setDetectMode(e.target.value), children: [_jsx("option", { value: 'camera', children: "Camera feed \u2192 mapped overlay (new)" }), _jsx("option", { value: 'composite', children: "Screen composite (legacy)" })] }), _jsx("p", { className: 'mt-1 text-[11px] leading-snug text-dash-muted', children: detectMode === 'camera'
                                    ? 'Detects on the raw webcam, then maps the mesh onto the PiP box (camBox + mirror) — the live feature + report overlay path.'
                                    : 'Detects on the whole downscaled screen composite (the old behaviour).' })] }), detectMode === 'camera' && (_jsxs("label", { className: 'flex items-center gap-2 text-xs text-dash-text', children: [_jsx("input", { type: 'checkbox', className: 'accent-primary', checked: camVisibleSim, onChange: (e) => setCamVisibleSim(e.target.checked) }), "Camera visible in recording (uncheck \u2192 overlay hidden)"] })), _jsxs("div", { children: [_jsx("label", { className: labelCls, children: "Candidate screen" }), _jsx("select", { className: selectCls, value: screenIdx, onChange: (e) => setScreenIdx(Number(e.target.value)), children: SCREEN_PRESETS.map((s, i) => (_jsx("option", { value: i, children: s.label }, s.label))) })] }), _jsxs("div", { children: [_jsx("label", { className: labelCls, children: "Capture width (downscale)" }), _jsx("select", { className: selectCls, value: captureW, onChange: (e) => setCaptureW(Number(e.target.value)), children: WIDTH_OPTIONS.map((w) => (_jsxs("option", { value: w, children: [w, "px", w === 320 ? ' (current live default)' : ''] }, w))) })] }), _jsxs("div", { children: [_jsxs("label", { className: labelCls, children: ["Webcam PiP width: ", _jsxs("span", { className: 'font-semibold text-dash-text', children: [pipW, "px"] })] }), _jsx("input", { type: 'range', min: 120, max: 900, step: 10, value: pipW, onChange: (e) => setPipW(Number(e.target.value)), className: 'w-full accent-primary' }), _jsx("button", { type: 'button', className: 'mt-1 text-xs text-dash-accentInk hover:underline', onClick: () => setPipW(CODING_PIP_WIDTH), children: "Set to coding-editor size (340px)" })] }), _jsxs("div", { className: 'rounded-md bg-dash-surface px-3 py-2 text-xs text-dash-muted', children: ["Effective face area \u2248 a", ' ', _jsxs("span", { className: 'font-semibold text-dash-text', children: [pipInCapture, "px"] }), "-wide tile after downscaling."] }), _jsx("div", { className: 'mt-1 text-[11px] font-semibold uppercase tracking-wide text-dash-muted', children: "Live config" }), _jsxs("div", { className: 'flex flex-col gap-2', children: [_jsxs(Button, { variant: 'outline', onClick: () => setStreaming((v) => !v), disabled: camState !== 'on' || wsState !== 'ready', className: 'w-full', children: [streaming ? _jsx(Pause, { className: 'mr-1 h-4 w-4' }) : _jsx(Play, { className: 'mr-1 h-4 w-4' }), streaming ? 'Pause' : 'Resume'] }), _jsx(Button, { variant: 'outline', onClick: logCurrent, disabled: !last, className: 'w-full', children: "Log current result" }), _jsxs("div", { className: 'mt-1 flex items-center justify-between rounded-md border border-dash-border px-3 py-2', children: [_jsxs("span", { className: 'text-xs text-dash-muted', children: ["Live width:", ' ', _jsxs("span", { className: 'font-semibold text-dash-text', children: [(_a = liveConfig === null || liveConfig === void 0 ? void 0 : liveConfig.capture_width) !== null && _a !== void 0 ? _a : 320, "px"] })] }), _jsxs(Button, { size: 'sm', variant: 'outline', onClick: () => void saveAsLive(), disabled: updateConfig.isPending, children: [updateConfig.isPending ? (_jsx(Loader2, { className: 'mr-1 h-4 w-4 animate-spin' })) : (_jsx(Save, { className: 'mr-1 h-4 w-4' })), "Save ", captureW, "px"] })] })] }), trials.length > 0 && (_jsx("div", { className: 'overflow-x-auto rounded-lg border border-dash-border', children: _jsxs("table", { className: 'min-w-full divide-y divide-dash-border text-xs', children: [_jsx("thead", { className: 'bg-dash-surface text-dash-muted', children: _jsxs("tr", { children: [_jsx("th", { className: 'px-2 py-1.5 text-left', children: "PiP" }), _jsx("th", { className: 'px-2 py-1.5 text-left', children: "Cap" }), _jsx("th", { className: 'px-2 py-1.5 text-left', children: "Faces" }), _jsx("th", { className: 'px-2 py-1.5 text-left', children: "Result" })] }) }), _jsx("tbody", { className: 'divide-y divide-dash-border', children: trials.map((t) => (_jsxs("tr", { children: [_jsx("td", { className: 'px-2 py-1', children: t.pipW }), _jsx("td", { className: 'px-2 py-1', children: t.captureW }), _jsx("td", { className: 'px-2 py-1', children: t.faceCount }), _jsx("td", { className: `px-2 py-1 font-medium ${t.detected ? 'text-dash-successInk' : 'text-dash-dangerInk'}`, children: t.detected ? 'detected' : 'none' })] }, t.id))) })] }) }))] }), _jsxs("main", { className: 'relative flex min-w-0 flex-1 items-center justify-center overflow-hidden bg-slate-950 p-6', children: [_jsx("div", { className: 'pointer-events-none absolute inset-0 opacity-[0.07]', style: {
                            backgroundImage: 'linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)',
                            backgroundSize: '32px 32px',
                        } }), camState !== 'on' ? (_jsxs("div", { className: 'relative flex max-w-md flex-col items-center gap-5 rounded-2xl border border-dashed border-white/15 bg-white/[0.03] px-10 py-12 text-center', children: [_jsx("div", { className: 'flex h-16 w-16 items-center justify-center rounded-full bg-dash-accent/15 text-dash-accentInk ring-1 ring-dash-accent/30', children: _jsx(Camera, { className: 'h-7 w-7' }) }), _jsxs("div", { children: [_jsx("h3", { className: 'text-base font-semibold text-white', children: "Live proctoring test" }), _jsx("p", { className: 'mt-1.5 text-sm leading-relaxed text-slate-400', children: "Start your camera to stream a simulated interview screen to the backend face detector and watch the live mesh overlay." })] }), _jsxs(Button, { onClick: startCam, disabled: camState === 'starting', children: [camState === 'starting' ? (_jsx(Loader2, { className: 'mr-1.5 h-4 w-4 animate-spin' })) : (_jsx(Camera, { className: 'mr-1.5 h-4 w-4' })), "Start camera"] }), camState === 'error' && _jsx("p", { className: 'text-xs text-dash-dangerInk', children: camError })] })) : (_jsxs(_Fragment, { children: [_jsx("canvas", { ref: screenCanvasRef, className: 'relative max-h-full max-w-full rounded-xl border border-white/10 bg-black shadow-2xl ring-1 ring-black/40' }), last && (_jsxs("div", { className: `absolute left-8 top-8 inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold shadow-lg backdrop-blur ${last.detected ? 'bg-dash-success/90 text-white' : 'bg-dash-danger/90 text-white'}`, children: [last.detected ? (_jsx(CheckCircle2, { className: 'h-4 w-4' })) : (_jsx(XCircle, { className: 'h-4 w-4' })), last.detected ? 'FACE DETECTED' : 'NOT DETECTED', _jsxs("span", { className: 'font-normal tabular-nums opacity-90', children: ["\u00B7 ", (_b = last.face_count) !== null && _b !== void 0 ? _b : 0, " face \u00B7 ", (_c = last.elapsed_ms) !== null && _c !== void 0 ? _c : 0, "ms"] })] }))] })), _jsx("video", { ref: videoRef, autoPlay: true, muted: true, playsInline: true, className: 'hidden' }), _jsx("canvas", { ref: captureCanvasRef, className: 'hidden' })] })] }));
};
// Report-overlay parity: same landmark subsets + corner-bracket boundary the
// report's ProctoringVideoOverlay draws, so the bench looks identical to the
// real report.
const FACE_OVAL = [10, 152, 234, 454, 21, 251, 172, 397];
const LEFT_IRIS_IDS = [468, 469, 470, 471, 472];
const RIGHT_IRIS_IDS = [473, 474, 475, 476, 477];
const avgPt = (face, ids) => {
    const pts = ids.map((i) => face[String(i)]).filter(Boolean);
    if (!pts.length)
        return null;
    return [
        pts.reduce((s, p) => s + p[0], 0) / pts.length,
        pts.reduce((s, p) => s + p[1], 0) / pts.length,
    ];
};
// Rounded corner brackets (four L-shaped corners) — identical to the report.
function drawCornerBrackets(ctx, x0, y0, x1, y1, color, lineWidth) {
    const w = x1 - x0;
    const h = y1 - y0;
    if (w <= 0 || h <= 0)
        return;
    const len = Math.max(10, Math.min(w, h) * 0.24);
    const r = Math.min(len * 0.5, 14);
    const corners = [
        [x0, y0, 1, 1],
        [x1, y0, -1, 1],
        [x1, y1, -1, -1],
        [x0, y1, 1, -1],
    ];
    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    corners.forEach(([cx, cy, sx, sy]) => {
        ctx.moveTo(cx, cy + sy * len);
        ctx.arcTo(cx, cy, cx + sx * len, cy, r);
        ctx.lineTo(cx + sx * len, cy);
    });
    ctx.stroke();
    ctx.restore();
}
// Draw the curated face landmarks EXACTLY like the report overlay: a rounded
// corner-bracket boundary from the PRIMARY face's oval (so multiple faces never
// merge into one big box — the backend returns only the primary face, and the
// box is built from its oval), small cyan curated dots, small pink iris, and
// green gaze arrows. `toXY` maps normalized (0..1) → canvas px (identity in
// composite mode, camBox+mirror compose in camera mode). `border` tints the
// boundary (e.g. red when a violation like multiple-faces is active).
function drawMesh(ctx, face, toXY, border) {
    if (!face || !Object.keys(face).length)
        return;
    // Boundary from the primary face's oval extremes (mapped to px first so a
    // mirror can't invert the box).
    const oval = FACE_OVAL.map((i) => face[String(i)]).filter(Boolean);
    if (oval.length >= 3) {
        const pxs = oval.map((p) => toXY(p[0], p[1])[0]);
        const pys = oval.map((p) => toXY(p[0], p[1])[1]);
        const pad = 6;
        drawCornerBrackets(ctx, Math.min(...pxs) - pad, Math.min(...pys) - pad, Math.max(...pxs) + pad, Math.max(...pys) + pad, border, 2);
    }
    // Curated points (cyan) — small.
    ctx.fillStyle = 'rgba(34,211,238,0.85)';
    Object.values(face).forEach((p) => {
        if (!p)
            return;
        const [x, y] = toXY(p[0], p[1]);
        ctx.beginPath();
        ctx.arc(x, y, 1.6, 0, Math.PI * 2);
        ctx.fill();
    });
    // Iris highlight (pink) — small, matches the report's 2.4px.
    ctx.fillStyle = '#f472b6';
    [...LEFT_IRIS_IDS, ...RIGHT_IRIS_IDS].forEach((i) => {
        const p = face[String(i)];
        if (!p)
            return;
        const [x, y] = toXY(p[0], p[1]);
        ctx.beginPath();
        ctx.arc(x, y, 2.4, 0, Math.PI * 2);
        ctx.fill();
    });
    // Gaze arrows (green) from each eye centre toward the iris offset.
    const eyePairs = [
        [avgPt(face, [33, 133]), avgPt(face, LEFT_IRIS_IDS)],
        [avgPt(face, [362, 263]), avgPt(face, RIGHT_IRIS_IDS)],
    ];
    ctx.strokeStyle = '#a3e635';
    ctx.lineWidth = 2;
    eyePairs.forEach(([center, iris]) => {
        if (!center || !iris)
            return;
        const dx = (iris[0] - center[0]) * 6;
        const dy = (iris[1] - center[1]) * 6;
        const [sx, sy] = toXY(center[0], center[1]);
        const [ex, ey] = toXY(center[0] + dx, center[1] + dy);
        ctx.beginPath();
        ctx.moveTo(sx, sy);
        ctx.lineTo(ex, ey);
        ctx.stroke();
    });
}
// Report-style HUD box (top-left of the drawn region): faces count + flags.
function drawHud(ctx, ox, oy, lines) {
    if (!lines.length)
        return;
    ctx.font = '13px ui-monospace, monospace';
    const pad = 6;
    const boxW = Math.max(...lines.map((l) => ctx.measureText(l).width)) + pad * 2;
    const boxH = lines.length * 17 + pad * 2;
    ctx.fillStyle = 'rgba(2,6,23,0.66)';
    ctx.fillRect(ox + 8, oy + 8, boxW, boxH);
    ctx.fillStyle = '#e2e8f0';
    lines.forEach((l, i) => ctx.fillText(l, ox + 8 + pad, oy + 8 + pad + 13 + i * 17));
}
export default ProctoringTestPanel;
